import { compact } from "lodash";
import moment from "moment";
import { observable } from "mobx";
import { classToPlain } from "class-transformer";
import { ArrayNotEmpty, ValidateNested, IsDate } from "class-validator";
import {
  PriceListFormInput,
  PriceGroupInput,
  ProfessionPrice,
  CustomerContractPriceListProfileFragment,
  ContractPriceTemplateRecordInput as IContractPriceTemplateRecordInput
} from "@graphql";
import { encodeProfAoeSpec } from "@bonliva-crm/common/ConsultantSearch";
import { createObjectID } from "@bonliva-crm/common";
import locale from "@bonliva-crm/locale";
import { ContractPriceTemplateRecordInput } from "../../config_editor/ContractsPriceTemplates/ContractsPriceTemplateFormInput";
import { mergeHighHolidays } from "../../config_editor/ContractsPriceTemplates/utils";

export class CustomerContractPriceListFormData implements PriceListFormInput {
  static CreateEmpty(travelZones: Array<Omit<TravelZoneData, "articleNumber">>) {
    const item = new CustomerContractPriceListFormData();
    item.travelZones = travelZones.map((x) => ({ ...x, articleNumber: "" }));

    return item;
  }

  static Create(
    data: CustomerContractPriceListProfileFragment,
    travelZones: Array<Omit<TravelZoneData, "articleNumber">>,
    isDuplicate?: boolean
  ): CustomerContractPriceListFormData {
    const item = new CustomerContractPriceListFormData();

    item.name = data.name;
    item.travelZones = travelZones.map((x) => ({ ...x, articleNumber: "" }));
    item.administrativeFee = data.administrativeFee;
    item.startDate = isDuplicate ? null : moment(data.startDate).toDate();
    item.endDate = isDuplicate ? null : moment(data.endDate).toDate();
    item.customerIds = compact(data.customers).map((x) => x._id);
    item.priceGroups = data.priceGroups.map(CustomerContractPriceGroupFormData.Create);

    for (const tz of data.travelZones) {
      const tzItem = item.travelZones.find((x) => x.name === tz.name);

      if (tzItem) {
        tzItem.articleNumber = tz.articleNumber;
      }
    }

    return item;
  }

  @observable
  name: string = "";

  @observable
  administrativeFee: number = 0;

  @observable
  @IsDate({ message: locale.fieldIsMandatory(locale.StartDate) })
  startDate: Date = null;

  @observable
  @IsDate({ message: locale.fieldIsMandatory(locale.EndDate) })
  endDate: Date = null;

  @observable
  customerIds: string[] = [];

  @observable
  priceGroups: CustomerContractPriceGroupFormData[] = [];

  @observable
  travelZones: TravelZoneData[] = [];

  getPlain() {
    const plain = classToPlain(this);
    const { priceGroups } = plain;
    const formatItemPrice: PriceGroupInput[] = priceGroups?.map(
      ({ professions, template, favTemplate, administrativeFee, ...rest }) => ({
        ...rest,
        records: rest.records.map(({ id, isFreeInput, ...x }) => ({
          ...x,
          paidPer: x.paidPer || null,
          currency: x.currency || null,
          workType: x.workType || null,
          period: x.period || null,
          from: x.from || null,
          to: x.to || null,
          endDay: x.endDay || null,
          startDay: x.startDay || null,
          fixedPrice: x.fixedPrice ? parseFloat(x.fixedPrice) : null,
          customPrice: x.customPrice ? parseFloat(x.customPrice) : null,
          priceMultiplier: x.priceMultiplier ? parseFloat(x.priceMultiplier) : null,
          isSecondPass: x.isSecondPass || null
        })),
        professionsPrice: rest.professionsPrice.map((el) => ({
          ...el,
          price: el.price ? parseFloat(el.price) : null
        }))
      })
    );

    return {
      ...plain,
      priceGroups: formatItemPrice,
      administrativeFee: plain.administrativeFee
        ? parseFloat(plain.administrativeFee)
        : null
    };
  }
}

export class CustomerContractPriceGroupFormData implements PriceGroupInput {
  static CreateEmpty(highHolidayOptions, isBonlivaCare) {
    const item = new CustomerContractPriceGroupFormData();
    item._id = createObjectID();
    item.records = isBonlivaCare ? [] : [ContractPriceTemplateRecordInput.CreateBlank()];
    item.highHolidays = mergeHighHolidays(highHolidayOptions, []);
    item.professionsPrice = [];
    return item;
  }

  static Create(data: CustomerContractPriceListProfileFragment["priceGroups"][0]) {
    const item = new CustomerContractPriceGroupFormData();

    item._id = data._id;
    item.professionsPrice = data.professionsPrice?.map((x) => {
      return {
        profession: encodeProfAoeSpec(
          x.profession.name,
          x.profession.areaOfExpertise,
          x.profession.specialization
        ),
        price: x.price
      };
    });
    item.records = data.records;
    item.highHolidays = data.highHolidays;
    item.professions = data.professionsPrice.map((x) =>
      encodeProfAoeSpec(
        x.profession.name,
        x.profession.areaOfExpertise,
        x.profession.specialization
      )
    );

    return item;
  }

  @observable
  _id: string;

  @ArrayNotEmpty({ message: locale.Required })
  @ValidateNested()
  @observable
  records: IContractPriceTemplateRecordInput[] = [
    ContractPriceTemplateRecordInput.CreateBlank()
  ];

  @observable
  highHolidays = [];

  @observable
  professionsPrice: ProfessionPrice[];

  @observable
  template: string;

  @observable
  favTemplate: string;

  @observable
  professions: string[] = [];
}

export class TravelZoneData {
  label: string;

  name: string;

  @observable
  articleNumber: string;
}
